import { useState, useEffect, useRef } from "react";
import {
  MessageSquarePlus, Settings, Send, Sparkles, User, Trash2,
  Menu, Copy, Check, Eye, EyeOff, ArrowLeft, Plus
} from "lucide-react";

const C = {
  bg: "#09090B",
  sidebar: "#0F0F12",
  surface: "#17171C",
  surfaceHov: "#1E1E25",
  border: "#27272F",
  borderHov: "#38383F",
  gold: "#C8A84B",
  goldDim: "#8A6E2A",
  goldGlow: "rgba(200,168,75,0.15)",
  text: "#EDEBE5",
  muted: "#7A7870",
  dim: "#3A3A40",
  err: "#D94F4F",
  ok: "#2E9E74",
};

const MODELS = [
  { id: "qwen3.6:27b", label: "Qwen 3.6 27B", badge: "Default" },
  { id: "qwen3.5:9b", label: "Qwen 3.5 9B", badge: "Fast" },
  { id: "qwen2.5:3b", label: "Qwen 2.5 3B", badge: "Lightning" },
  { id: "qwen2.5-coder:14b", label: "Qwen Coder 14B", badge: "Code" },
  { id: "granite4.1:8b", label: "Granite 4.1 8B", badge: "General" },
];

const DEFAULTS = {
  model: "qwen3.6:27b",
  temperature: 0.7,
  system: "Anda adalah AeroXlaw, asisten AI personal untuk Bos Andiie. Profesional, kompeten, dan berbicara dalam bahasa Indonesia yang sopan namun akrab. Ahli dalam pemrograman, manajemen proyek, dan otomasi.",
};

function uid() { return Math.random().toString(36).slice(2, 10); }
function ago(d) {
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/* ─── Markdown renderer ─── */
function renderMD(text, isUser) {
  const lines = text.split("\n");
  const elems = [];
  let codeLines = [], inCode = false, lang = "";
  let li = [], inList = false;

  const flushList = (key) => {
    if (li.length) {
      elems.push(
        <ul key={`ul-${key}`} style={{ margin: "8px 0", paddingLeft: 18 }}>
          {li.map((t, i) => (
            <li key={i} style={{ color: C.text, fontSize: 14.5, lineHeight: 1.7, marginBottom: 2 }}>
              {inlineFormat(t)}
            </li>
          ))}
        </ul>
      );
      li = [];
    }
    inList = false;
  };

  const inlineFormat = (t) => {
    const parts = t.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**"))
        return <strong key={i} style={{ fontWeight: 600 }}>{p.slice(2, -2)}</strong>;
      if (p.startsWith("`") && p.endsWith("`"))
        return <code key={i} style={{ background: "rgba(200,168,75,0.12)", color: "#E8C87A", padding: "1px 5px", borderRadius: 4, fontSize: "0.88em", fontFamily: "IBM Plex Mono, monospace" }}>{p.slice(1, -1)}</code>;
      if (p.startsWith("*") && p.endsWith("*"))
        return <em key={i}>{p.slice(1, -1)}</em>;
      return p;
    });
  };

  lines.forEach((line, idx) => {
    if (line.startsWith("```")) {
      if (!inCode) { inCode = true; lang = line.slice(3); codeLines = []; }
      else {
        inCode = false;
        if (inList) flushList(idx);
        elems.push(
          <div key={`code-${idx}`} style={{ margin: "12px 0", borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
            {lang && <div style={{ padding: "5px 14px", background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: C.muted, fontSize: 11, fontFamily: "monospace" }}>{lang}</span>
            </div>}
            <pre style={{ margin: 0, padding: "14px", background: "#0C0C0F", overflowX: "auto", fontSize: 13, color: "#A9B7C6", fontFamily: "IBM Plex Mono, monospace", lineHeight: 1.6 }}>
              {codeLines.join("\n")}
            </pre>
          </div>
        );
        codeLines = [];
      }
      return;
    }
    if (inCode) { codeLines.push(line); return; }

    if (line.match(/^#{1,3} /)) {
      if (inList) flushList(idx);
      const lv = line.match(/^(#+) /)[1].length;
      const sizes = [20, 17, 15];
      elems.push(<p key={idx} style={{ color: C.text, fontSize: sizes[lv - 1] || 14, fontWeight: 600, margin: "14px 0 6px" }}>{inlineFormat(line.replace(/^#+\s/, ""))}</p>);
      return;
    }
    if (line.match(/^[-*] /) || line.match(/^\d+\. /)) {
      inList = true;
      li.push(line.replace(/^[-*] /, "").replace(/^\d+\. /, ""));
      return;
    }
    if (inList && line === "") { flushList(idx); return; }
    if (inList && !line.match(/^[-*] /) && !line.match(/^\d+\. /)) flushList(idx);

    if (line === "") { elems.push(<div key={idx} style={{ height: 6 }} />); return; }
    elems.push(
      <p key={idx} style={{ color: isUser ? "rgba(9,9,11,0.9)" : C.text, margin: "0 0 2px", lineHeight: 1.72, fontSize: 14.5 }}>
        {inlineFormat(line)}
      </p>
    );
  });
  if (inList) flushList("end");
  return elems;
}

/* ─── Message Bubble ─── */
function Bubble({ msg, streaming }) {
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === "user";

  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 22, animation: "fadeUp 0.2s ease", alignItems: "flex-start", flexDirection: isUser ? "row-reverse" : "row" }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, flexShrink: 0, marginTop: 2,
        background: isUser ? C.gold : C.surface, border: isUser ? "none" : `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {isUser ? <User size={16} color={C.bg} /> : <Sparkles size={16} color={C.gold} />}
      </div>

      <div style={{ maxWidth: "76%", display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: isUser ? C.gold : C.muted, marginBottom: 5, letterSpacing: "0.4px" }}>
          {isUser ? "You" : "Assistant"}
        </span>

        <div style={{
          padding: "12px 16px",
          background: isUser ? C.gold : C.surface,
          border: isUser ? "none" : `1px solid ${C.border}`,
          borderRadius: isUser ? "12px 3px 12px 12px" : "3px 12px 12px 12px",
        }}>
          {renderMD(msg.content, isUser)}
          {streaming && (
            <span style={{ display: "inline-block", width: 7, height: 15, background: C.gold, borderRadius: 2, marginLeft: 2, verticalAlign: "text-bottom", animation: "blink 0.85s step-end infinite" }} />
          )}
        </div>

        {!isUser && !streaming && msg.content && (
          <button
            onClick={() => { navigator.clipboard.writeText(msg.content); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            style={{ marginTop: 6, padding: "3px 8px", borderRadius: 6, background: "none", border: `1px solid ${C.dim}`, color: C.muted, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 4, transition: "border-color 0.2s" }}
          >
            {copied ? <><Check size={10} />Copied</> : <><Copy size={10} />Copy</>}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Settings Page ─── */
function SettingsPage({ cfg, setCfg, apiKey, setApiKey, onBack }) {
  const [local, setLocal] = useState(cfg);
  const [key, setKey] = useState(apiKey);
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = () => { setCfg(local); setApiKey(key); setSaved(true); setTimeout(() => setSaved(false), 2200); };

  const Row = ({ label, hint, children }) => (
    <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div>
          <p style={{ color: C.text, fontSize: 14, fontWeight: 500, margin: "0 0 3px" }}>{label}</p>
          {hint && <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>{hint}</p>}
        </div>
        {children}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${C.border}`, background: C.sidebar, display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onBack} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 12px", color: C.muted, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <ArrowLeft size={14} /> Back
        </button>
        <h2 style={{ color: C.text, fontSize: 16, fontWeight: 600, margin: 0 }}>Settings</h2>
      </div>

      <div style={{ maxWidth: 580, margin: "0 auto", padding: "36px 24px" }}>

        {/* Status */}
        <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 10px" }}>Status</p>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 28, overflow: "hidden" }}>
          <Row label="Ollama Engine" hint="Local AI on your machine">
            <span style={{ color: C.ok, fontSize: 13, fontWeight: 600 }}>● Connected</span>
          </Row>
          <Row label="Supabase DB" hint="Cloud storage">  
            <span style={{ color: C.ok, fontSize: 13, fontWeight: 600 }}>● Connected</span>
          </Row>
        </div>

        {/* Model */}
        <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 10px" }}>Model</p>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 28, overflow: "hidden" }}>
          {MODELS.map((m, i) => (
            <div key={m.id} onClick={() => setLocal(s => ({ ...s, model: m.id }))}
              style={{ padding: "14px 20px", borderBottom: i < MODELS.length - 1 ? `1px solid ${C.border}` : "none", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", background: local.model === m.id ? C.goldGlow : "transparent", transition: "background 0.15s" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${local.model === m.id ? C.gold : C.border}`, background: local.model === m.id ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {local.model === m.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.bg }} />}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: local.model === m.id ? C.gold : C.text, fontSize: 14, fontWeight: 500, margin: "0 0 2px" }}>{m.label}</p>
                <p style={{ color: C.muted, fontSize: 11, margin: 0, fontFamily: "monospace" }}>{m.id}</p>
              </div>
              <span style={{ background: C.dim, color: C.muted, fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 20 }}>{m.badge}</span>
            </div>
          ))}
        </div>

        {/* System prompt */}
        <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 10px" }}>System Prompt</p>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 28 }}>
          <textarea value={local.system} onChange={e => setLocal(s => ({ ...s, system: e.target.value }))} rows={4}
            style={{ width: "100%", padding: "10px 13px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 14, outline: "none", fontFamily: "inherit", resize: "vertical", lineHeight: 1.6, boxSizing: "border-box" }}
          />
        </div>

        {/* Temperature */}
        <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 10px" }}>
          Temperature — {local.temperature.toFixed(1)}
        </p>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 22px", marginBottom: 32 }}>
          <input type="range" min="0" max="1" step="0.1" value={local.temperature}
            onChange={e => setLocal(s => ({ ...s, temperature: +e.target.value }))}
            style={{ width: "100%", accentColor: C.gold }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ color: C.muted, fontSize: 11 }}>0.0 — Precise</span>
            <span style={{ color: C.muted, fontSize: 11 }}>1.0 — Creative</span>
          </div>
        </div>

        <button onClick={save} style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", background: saved ? C.ok : C.gold, color: C.bg, fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit", transition: "background 0.3s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {saved ? <><Check size={17} /> Saved!</> : "Save Settings"}
        </button>

      </div>
    </div>
  );
}

/* ─── Empty state suggestions ─── */
const SUGGESTIONS = [
  "Explain quantum computing simply",
  "Write a Python function to merge PDFs",
  "Help me brainstorm a startup idea",
  "Translate this to Japanese: 'Good morning!'",
];

/* ─── Main Chat ─── */
function ChatMain({ sessions, setSessions, curId, setCurId, cfg, onNav, streaming, setStreaming }) {
  const [input, setInput] = useState("");
  const [sidebar, setSidebar] = useState(true);
  const endRef = useRef(null);
  const taRef = useRef(null);
  const sessionsRef = useRef(sessions);
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);

  const curSess = sessions.find(s => s.id === curId);
  const msgs = curSess?.messages || [];

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, streaming]);

  const newChat = () => {
    const id = uid();
    setSessions(prev => [{ id, title: "New conversation", messages: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }, ...prev]);
    setCurId(id);
    setInput("");
  };

  const send = async (text) => {
    text = text.trim();
    if (!text || streaming) return;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";

    let sid = curId;
    let baseMessages = [];

    if (!sid) {
      sid = uid();
      const ns = { id: sid, title: text.slice(0, 42), messages: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      setSessions(prev => [ns, ...prev]);
      setCurId(sid);
    } else {
      baseMessages = (sessionsRef.current.find(s => s.id === sid)?.messages) || [];
    }

    const userMsg = { role: "user", content: text };
    const allMsgs = [...baseMessages, userMsg];

    setSessions(prev => prev.map(s => s.id !== sid ? s : {
      ...s,
      title: s.messages.length === 0 ? text.slice(0, 42) : s.title,
      messages: [...s.messages, userMsg],
      updatedAt: new Date().toISOString(),
    }));

    try {
      setStreaming("");
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid,
          messages: allMsgs,
          model: cfg.model,
          temperature: cfg.temperature,
          stream: true,
        }),
      });

      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.error || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              full += parsed.content;
              setStreaming(full);
            }
          } catch {}
        }
      }

      setSessions(prev => prev.map(s => s.id !== sid ? s : {
        ...s,
        messages: [...s.messages, { role: "assistant", content: full || "(empty response)" }],
        updatedAt: new Date().toISOString(),
      }));
    } catch (err) {
      setSessions(prev => prev.map(s => s.id !== sid ? s : {
        ...s,
        messages: [...s.messages, { role: "assistant", content: `**Error:** ${err.message}\n\nPlease check your API key in **Settings**.` }],
        updatedAt: new Date().toISOString(),
      }));
    } finally {
      setStreaming(null);
    }
  };

  const ta = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 180) + "px";
  };

  return (
    <div style={{ display: "flex", height: "100vh", background: C.bg, fontFamily: "system-ui, sans-serif", overflow: "hidden" }}>
      {/* Sidebar */}
      <div style={{ width: sidebar ? 252 : 0, flexShrink: 0, background: C.sidebar, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", overflow: "hidden", transition: "width 0.22s ease" }}>
        <div style={{ padding: "16px 14px 12px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 13 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: C.gold, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sparkles size={15} color={C.bg} />
            </div>
            <span style={{ color: C.text, fontWeight: 700, fontSize: 14, letterSpacing: "-0.2px" }}>PAWS</span>
          </div>
          <button onClick={newChat} style={{ width: "100%", padding: "8px 12px", background: C.goldGlow, border: `1px solid ${C.goldDim}`, borderRadius: 8, color: C.gold, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, fontFamily: "inherit", fontSize: 13, fontWeight: 500 }}>
            <Plus size={14} /> New chat
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "6px 8px" }}>
          {sessions.length === 0 && (
            <p style={{ color: C.dim, fontSize: 12.5, textAlign: "center", marginTop: 28, lineHeight: 1.6 }}>No conversations yet.<br />Start a new chat!</p>
          )}
          {sessions.map(s => (
            <div key={s.id} onClick={() => setCurId(s.id)}
              style={{ padding: "9px 10px", borderRadius: 8, marginBottom: 2, background: s.id === curId ? C.goldGlow : "transparent", border: `1px solid ${s.id === curId ? C.goldDim : "transparent"}`, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, transition: "all 0.14s" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: s.id === curId ? "#E8C87A" : C.text, fontSize: 12.5, fontWeight: 500, margin: "0 0 3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</p>
                <p style={{ color: C.dim, fontSize: 10.5, margin: 0 }}>{ago(s.updatedAt)} · {s.messages.length} msgs</p>
              </div>
              <button onClick={e => { e.stopPropagation(); setSessions(prev => prev.filter(x => x.id !== s.id)); if (curId === s.id) setCurId(null); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: C.dim, padding: 2, borderRadius: 4, opacity: 0, transition: "opacity 0.2s", flexShrink: 0 }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ padding: "10px 8px", borderTop: `1px solid ${C.border}` }}>
          <button onClick={() => onNav("settings")} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 9, color: C.muted, fontFamily: "inherit", fontSize: 13 }}>
            <Settings size={14} /> Settings
          </button>

        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Topbar */}
        <div style={{ padding: "13px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0, background: C.bg }}>
          <button onClick={() => setSidebar(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, padding: 4, borderRadius: 6 }}>
            <Menu size={17} />
          </button>
          <div style={{ flex: 1 }}>
            <p style={{ color: C.text, fontSize: 14.5, fontWeight: 600, margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {curSess?.title || "New conversation"}
            </p>
            <p style={{ color: C.dim, fontSize: 11, margin: 0, fontFamily: "monospace" }}>{cfg.model}</p>
          </div>
          {curSess && (
            <button onClick={newChat} title="New chat" style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px", color: C.muted, cursor: "pointer" }}>
              <MessageSquarePlus size={15} />
            </button>
          )}
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflow: "auto", padding: "28px 20px" }}>
          {msgs.length === 0 && !streaming && (
            <div style={{ maxWidth: 560, margin: "12vh auto 0", textAlign: "center" }}>
              <div style={{ width: 60, height: 60, borderRadius: 16, background: C.goldGlow, border: `1px solid ${C.goldDim}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <Sparkles size={30} color={C.gold} />
              </div>
              <h2 style={{ color: C.text, fontSize: 22, fontWeight: 700, margin: "0 0 10px", letterSpacing: "-0.4px" }}>What can I help with?</h2>
              <p style={{ color: C.muted, fontSize: 14.5, margin: "0 0 28px" }}>Ask anything — I'm ready to assist.</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => send(s)}
                    style={{ padding: "11px 14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 13, textAlign: "left", lineHeight: 1.4, transition: "border-color 0.15s, color 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.goldDim; e.currentTarget.style.color = C.text; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ maxWidth: 700, margin: "0 auto" }}>
            {msgs.map((m, i) => <Bubble key={i} msg={m} streaming={false} />)}
            {streaming !== null && streaming !== undefined && (
              <Bubble msg={{ role: "assistant", content: streaming || "" }} streaming />
            )}
            <div ref={endRef} />
          </div>
        </div>

        {/* Input */}
        <div style={{ padding: "14px 20px 20px", background: C.bg, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ maxWidth: 700, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 9, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 13, padding: "9px 12px", transition: "border-color 0.2s" }}
              onFocusCapture={e => e.currentTarget.style.borderColor = C.goldDim}
              onBlurCapture={e => e.currentTarget.style.borderColor = C.border}
            >
              <textarea ref={taRef} value={input} onChange={ta} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder="Send a message…" rows={1} disabled={!!streaming}
                style={{ flex: 1, background: "none", border: "none", outline: "none", color: C.text, fontSize: 14.5, fontFamily: "inherit", resize: "none", lineHeight: 1.6, maxHeight: 180, overflow: "auto" }}
              />
              <button onClick={() => send(input)} disabled={!input.trim() || !!streaming}
                style={{ width: 34, height: 34, borderRadius: 9, border: "none", background: input.trim() && !streaming ? C.gold : C.dim, display: "flex", alignItems: "center", justifyContent: "center", cursor: input.trim() && !streaming ? "pointer" : "default", flexShrink: 0, transition: "background 0.2s" }}>
                <Send size={15} color={input.trim() && !streaming ? C.bg : C.muted} />
              </button>
            </div>
            <p style={{ color: C.dim, fontSize: 11, textAlign: "center", margin: "9px 0 0" }}>
Enter to send · Shift+Enter new line · Powered by Ollama
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Root ─── */
export default function App() {
  const [page, setPage] = useState("chat");
  const [sessions, setSessions] = useState([]);
  const [curId, setCurId] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [cfg, setCfg] = useState(DEFAULTS);
  const [streaming, setStreaming] = useState(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeUp{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
      @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
      *{box-sizing:border-box}
      ::-webkit-scrollbar{width:4px}
      ::-webkit-scrollbar-track{background:transparent}
      ::-webkit-scrollbar-thumb{background:#27272F;border-radius:4px}
      textarea::placeholder,input::placeholder{color:#3A3A40!important}
      input[type=range]{cursor:pointer}
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (page === "settings") return (
    <SettingsPage
      cfg={cfg} setCfg={setCfg}
      apiKey={apiKey} setApiKey={setApiKey}
      onBack={() => setPage("chat")}
    />
  );

  return (
    <ChatMain
      sessions={sessions} setSessions={setSessions}
      curId={curId} setCurId={setCurId}
      cfg={cfg} onNav={setPage}
      streaming={streaming} setStreaming={setStreaming}
    />
  );
}
